import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { personalInfo } from '../config/personalInfo';
import { getHomeSeo } from '../config/seo';

import esTranslations from '../locales/es.json';
import enTranslations from '../locales/en.json';

export type Language = 'es' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  es: esTranslations,
  en: enTranslations,
};

const getNestedValue = (obj: any, path: string): string => {
  return (
    path.split('.').reduce((current, key) => {
      if (current && typeof current === 'object' && key in current) {
        return current[key];
      }
      return undefined;
    }, obj) || path
  );
};

const readLanguageFromUrl = (): Language | null => {
  if (typeof window === 'undefined') return null;
  const param = new URLSearchParams(window.location.search).get('lang');
  if (param === 'es' || param === 'en') return param;
  return null;
};

const syncLanguageToUrl = (lang: Language) => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (lang === 'es') {
    url.searchParams.set('lang', 'es');
  } else {
    url.searchParams.delete('lang');
  }
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const fromUrl = readLanguageFromUrl();
    if (fromUrl) return fromUrl;
    const savedLanguage = localStorage.getItem('portfolio-language') as Language;
    return savedLanguage || (personalInfo.defaultLanguage as Language) || 'en';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('portfolio-language', lang);
    document.documentElement.lang = lang;
    syncLanguageToUrl(lang);
  };

  const t = (key: string): string => getNestedValue(translations[language], key);

  useEffect(() => {
    document.documentElement.lang = language;
    // Keep shareable /?lang=es in sync for crawlers & social previews
    if (window.location.pathname === '/') {
      syncLanguageToUrl(language);
    }
  }, [language]);

  useEffect(() => {
    // Warm description fallback before React Helmet hydrates
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', getHomeSeo(language).description);
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
